/**
 * FilterPills — grupo de pills para filtros segmentados (status, tipo etc.).
 *
 * Visual referência: painel-screens.jsx:227-237 (lista de campanhas).
 * - Item ativo: bg --color-primary-soft + border --color-primary-soft-border + texto --color-primary
 * - Item inativo: border --color-border + texto --color-muted-foreground
 * - Padding 6px 12px, radius 6, font-size 12, weight 500
 *
 * Genérico em T (string union) para tipar value/onChange estritamente.
 */

import { cn } from "@/lib/utils";

export interface FilterPillOption<T extends string> {
  label: string;
  value: T;
  count?: number;
}

export interface FilterPillsProps<T extends string> {
  options: readonly FilterPillOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

export function FilterPills<T extends string>({
  options,
  value,
  onChange,
  className,
}: FilterPillsProps<T>) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
              active
                ? "border border-[var(--color-primary-soft-border)] bg-[var(--color-primary-soft)] text-[var(--color-primary)]"
                : "border border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
            )}
          >
            {opt.label}
            {opt.count !== undefined ? ` (${opt.count})` : ""}
          </button>
        );
      })}
    </div>
  );
}
